import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Brain, CalendarClock, ChevronLeft, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TimerMode } from '@/types/task';

interface TimerModeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (mode: TimerMode, duration: number | null) => void;
  taskName: string;
  defaultDuration?: number;
}

const MANAGER_DURATION_OPTIONS = [
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
];

const MAKER_GOAL_OPTIONS = [
  { label: '1 hr', value: 60 },
  { label: '2 hrs', value: 120 },
  { label: '3 hrs', value: 180 },
  { label: '4 hrs', value: 240 },
  { label: '5 hrs', value: 300 },
  { label: '6 hrs', value: 360 },
];

export function TimerModeDialog({ 
  open, 
  onOpenChange, 
  onSelect, 
  taskName,
  defaultDuration 
}: TimerModeDialogProps) {
  const [step, setStep] = useState<'mode' | 'duration' | 'goal'>('mode');
  const [selectedMode, setSelectedMode] = useState<TimerMode | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(defaultDuration || 30);
  const [selectedGoal, setSelectedGoal] = useState<number>(60);

  const handleModeSelect = (mode: TimerMode) => {
    setSelectedMode(mode);
    
    if (mode === 'maker') {
      // Maker mode: Show goal selection (1–6 hrs)
      setStep('goal');
    } else {
      // Manager mode: Need duration selection
      setStep('duration');
    }
  };

  const handleGoalSelect = () => {
    if (selectedMode === 'maker') {
      onSelect('maker', selectedGoal);
      resetState();
    }
  };

  const handleStart = () => {
    if (selectedMode === 'manager') {
      onSelect('manager', selectedDuration);
      resetState();
    }
  };

  const handleBack = () => {
    setStep('mode');
  };

  const resetState = () => {
    setStep('mode');
    setSelectedMode(null);
    setSelectedGoal(60);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-tour-target="timer-mode-select">
        {step === 'mode' ? (
          <>
            <DialogHeader>
              <DialogTitle>Choose your work mode</DialogTitle>
              <DialogDescription className="text-sm">
                <span className="font-medium text-foreground">{taskName}</span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-3 mt-4">
              {/* Maker's Schedule Mode */}
              <button
                onClick={() => handleModeSelect('maker')}
                className={cn(
                  "flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left",
                  "hover:border-primary hover:bg-primary/5",
                  "border-border bg-card"
                )}
              >
                <div className="p-3 rounded-full bg-primary/10 shrink-0">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground">Maker's Schedule</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Uninterrupted flow state. Timer counts up, no alarms. End when you're done.
                  </p>
                  <p className="text-xs text-primary/80 mt-2">
                    <Sparkles className="h-3 w-3 inline mr-1" />
                    Best for deep creative or strategic work
                  </p>
                </div>
              </button>
              
              {/* Manager Schedule Mode */}
              <button
                onClick={() => handleModeSelect('manager')}
                className={cn(
                  "flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left",
                  "hover:border-secondary hover:bg-secondary/5",
                  "border-border bg-card"
                )}
              >
                <div className="p-3 rounded-full bg-secondary shrink-0">
                  <CalendarClock className="h-6 w-6 text-secondary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground">Manager Schedule</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Time-boxed work blocks. Countdown creates urgency to finish on time.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    📋 Ideal for meetings, admin, and processing tasks
                  </p>
                </div>
              </button>
            </div>
          </>
        ) : step === 'goal' ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setStep('mode'); setSelectedMode(null); }}
                  className="p-1 rounded-lg hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div>
                  <DialogTitle>What&apos;s your goal?</DialogTitle>
                  <DialogDescription className="text-sm">
                    🧠 Deep Focus • {taskName}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            <p className="text-sm text-muted-foreground mt-2">
              Milestones will be based on your chosen goal
            </p>
            
            <div className="grid grid-cols-3 gap-3 mt-4">
              {MAKER_GOAL_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedGoal(option.value)}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all text-center font-medium",
                    selectedGoal === option.value
                      ? "border-primary bg-primary/20 text-foreground font-semibold shadow-md"
                      : "border-border/50 hover:border-primary/50 hover:bg-muted/50 text-muted-foreground"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            <Button 
              onClick={handleGoalSelect} 
              className="w-full mt-4 h-11"
              size="lg"
            >
              Start Deep Focus
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBack}
                  className="p-1 rounded-lg hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div>
                  <DialogTitle>Set time limit</DialogTitle>
                  <DialogDescription className="text-sm">
                    📋 Manager Schedule • {taskName}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            <div className="grid grid-cols-3 gap-3 mt-4">
              {MANAGER_DURATION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedDuration(option.value)}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all text-center font-medium",
                    selectedDuration === option.value
                      ? "border-primary bg-primary/20 text-foreground font-semibold shadow-md"
                      : "border-border/50 hover:border-primary/50 hover:bg-muted/50 text-muted-foreground"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            <Button 
              onClick={handleStart} 
              className="w-full mt-4 h-11"
              size="lg"
            >
              Start {selectedDuration} minute session
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
